/**
 * Tag fit engine — compute how to fit a jewellery tag into an EXACT physical
 * label size (width x height in mm). Because the whole tag scales uniformly by
 * a single factor `s`, a tag rendered at s=1 is geometrically similar to the
 * same tag at any `s` — so from one natural measurement (w,h at s=1) we can
 * derive the largest `s` that fits the target box.
 *
 * The engine tries several layout "candidates" (classic with purity in the
 * header, classic with purity moved BELOW the image to reclaim vertical space,
 * 2-column and 3-column grids) and picks the one that fits at the LARGEST
 * scale (most readable). If even the best candidate would need to shrink below
 * a minimum readable floor, it reports `fits: false` so the caller can warn
 * the user (single tag = inline error; batch = skip / best-effort dialog).
 */

export const PX_PER_MM = 96 / 25.4; // CSS px per mm at 96dpi

export function mmToPx(mm) {
  return (Number(mm) || 0) * PX_PER_MM;
}

// Minimum uniform scale we still consider readable, and the cap so small
// content on a large label is not blown up to huge fonts.
export const FIT_FLOOR = 0.42;
export const FIT_MAX = 1.0;

// Layout candidates tried in order of preference for a dense-but-readable fit.
export const FIT_CANDIDATES = [
  { layoutId: 'classic', purityPos: 'belowImage' },
  { layoutId: 'classic', purityPos: 'header' },
  { layoutId: 'grid2', purityPos: 'header' },
  { layoutId: 'grid3', purityPos: 'header' },
];

/**
 * @param {Array<{layoutId,purityPos,w,h}>} measured natural sizes at s=1
 * @param {number} targetWpx
 * @param {number} targetHpx
 */
export function chooseFit(measured, targetWpx, targetHpx, opts = {}) {
  const floor = opts.floor ?? FIT_FLOOR;
  const max = opts.max ?? FIT_MAX;
  let best = null;
  for (const c of measured) {
    if (!c.w || !c.h) continue;
    const sRaw = Math.min(targetWpx / c.w, targetHpx / c.h);
    if (!best || sRaw > best.sRaw) best = { ...c, sRaw };
  }
  if (!best) {
    return { fits: false, scale: floor, layoutId: 'classic', purityPos: 'header', sRaw: 0, reason: 'not-measured' };
  }
  return {
    fits: best.sRaw >= floor,
    // When it fits, scale down (capped) to the box. When it does NOT fit, keep
    // the readable floor so content overflows the exact label box and is
    // visibly clipped — making it obvious that data would be skipped.
    scale: best.sRaw >= floor ? Math.min(best.sRaw, max) : floor,
    sRaw: best.sRaw,
    layoutId: best.layoutId,
    purityPos: best.purityPos,
    natW: best.w,
    natH: best.h,
  };
}

export function formatMm(w, h) {
  return `${Number(w) || 0}\u00d7${Number(h) || 0}mm`;
}
